'use client';

import React, { useEffect, useRef, useState } from 'react';
import styles from './envelope.module.css';
import { Appearance } from '../page.client';

// @ts-ignore
import "./../../../../my-fonts.css";
import { FaEnvelope, FaEnvelopeOpen } from 'react-icons/fa6';

interface EnvelopeProps {
    appearance: Appearance;
    stampUrl: string;
    coverImage: string | null;
    isRotator?: boolean;
    title?: string;
    onHide: () => void;
    children?: React.ReactNode | null;
    blockReverse?: boolean;
    enableBirdIntro?: boolean;
    customLogo?: string | null;
    /** Hide the built-in open/close button (when the parent renders its own). */
    hideControl?: boolean;
    /** Change this number to open or close the envelope from outside. */
    toggleSignal?: number;
    /** Called when the envelope starts opening (true) or closes (false). */
    onOpenChange?: (open: boolean) => void;
}

const Envelope: React.FC<EnvelopeProps> = ({ appearance, stampUrl, coverImage, isRotator, title, onHide, children, blockReverse, enableBirdIntro = !isRotator, customLogo, hideControl, toggleSignal, onOpenChange }) => {
    const { envelopeColor, fontFamily, liningImage, textColor } = appearance;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isFlipped, setIsFlipped] = useState<boolean>(false);
    const [introActive, setIntroActive] = useState<boolean>(enableBirdIntro);

    const isGradient = envelopeColor.includes('gradient');
    const gradientMatches = isGradient ? envelopeColor.match(/#[a-fA-F0-9]{6}/g) : null;
    const gradStart = gradientMatches?.[0] || '#667eea';
    const gradEnd = gradientMatches?.[1] || '#764ba2';
    const fillStyle = isGradient ? 'url(#customEnvelopeGrad)' : 'var(--envelope-background-color)';
    const envelopeLogo = (typeof customLogo === 'string' && customLogo.trim() !== '')
        ? customLogo
        : '/assets/images/logos/lovingecards-logo.svg';

    function animateEnvelope() {
        if (!isOpen) {
            setIsFlipped(true);
            setTimeout(() => {
                setIsOpen(true);
            }, 1500);
            setTimeout(() => {
                onHide();
            }, 6000);
        } else if (isOpen && !blockReverse) {
            setIsOpen(false);
            setIsFlipped(false);
        }

    }

    // External open/close requests
    const lastSignal = useRef(toggleSignal);
    useEffect(() => {
        if (toggleSignal === undefined || toggleSignal === lastSignal.current) return;
        lastSignal.current = toggleSignal;
        animateEnvelope();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleSignal]);

    useEffect(() => {
        onOpenChange?.(isFlipped);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlipped]);

    useEffect(() => {
        if (enableBirdIntro) {
            const timer = setTimeout(() => {
                setIntroActive(false);
                if (!isRotator && !isFlipped) {
                    animateEnvelope();
                }
                
            }, 3800);
            return () => clearTimeout(timer);
        } else {
            if (!isRotator && !isFlipped) {
                const timer = setTimeout(() => {
                    animateEnvelope();
                }, 1000);
                return () => clearTimeout(timer);
            }
        }
    }, [enableBirdIntro]);


    return (
        <div className={styles.envelopeWrapper} style={{ '--envelope-background-color': envelopeColor } as React.CSSProperties}>
            {introActive && (
                <>
                    <div className={styles.skyBackground} />
                    <div className={styles.birdContainer}>
                        <svg viewBox="0 0 120 120" className={styles.birdSvg}>
                            <defs>
                                <linearGradient id="birdBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#ffffff" />
                                    <stop offset="60%" stopColor="#f8fafc" />
                                    <stop offset="100%" stopColor="#e2e8f0" />
                                </linearGradient>
                                <linearGradient id="birdWingGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#ffffff" />
                                    <stop offset="100%" stopColor="#cbd5e1" />
                                </linearGradient>
                                <linearGradient id="beakGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#fbbf24" />
                                    <stop offset="100%" stopColor="#f59e0b" />
                                </linearGradient>
                                <filter id="softShadow" x="-10%" y="-10%" width="120%" height="120%">
                                    <feDropShadow dx="1" dy="2" stdDeviation="1.5" floodColor="#475569" floodOpacity="0.15" />
                                </filter>
                            </defs>

                            {/* Far Wing (back wing) */}
                            <g className={styles.wingFar} filter="url(#softShadow)">
                                <path
                                    d="M 48 45 C 38 20, 22 8, 26 2 C 30 -2, 45 4, 52 25 C 55 18, 48 4, 53 2 C 58 0, 62 12, 60 30 C 64 22, 60 8, 65 8 C 70 8, 68 22, 62 38"
                                    fill="url(#birdWingGrad)"
                                    stroke="#cbd5e1"
                                    strokeWidth="0.75"
                                    strokeLinecap="round"
                                />
                            </g>

                            {/* Tail feathers */}
                            <path
                                d="M 38 62 C 28 65, 14 74, 10 75 C 8 75, 10 71, 16 68 C 12 66, 6 64, 8 62 C 10 60, 22 59, 32 58 Z"
                                fill="url(#birdBodyGrad)"
                                stroke="#cbd5e1"
                                strokeWidth="0.75"
                            />

                            {/* Unified Body and Head for smooth neck connection */}
                            <path
                                d="M 32 58 C 42 62, 58 60, 68 54 C 74 50, 78 44, 84 42 C 90 40, 94 34, 92 30 C 90 26, 84 28, 80 32 C 76 36, 70 42, 62 46 C 54 50, 42 52, 32 58 Z"
                                fill="url(#birdBodyGrad)"
                                stroke="#cbd5e1"
                                strokeWidth="0.75"
                                filter="url(#softShadow)"
                            />

                            {/* Eye */}
                            <circle cx="86" cy="32" r="1.5" fill="#0f172a" />

                            {/* Beak */}
                            <path d="M 91 30 L 98 33 L 90 35 Z" fill="url(#beakGrad)" />

                            {/* Near Wing (front wing) */}
                            <g className={styles.wingNear} filter="url(#softShadow)">
                                <path
                                    d="M 52 48 C 42 22, 34 2, 40 0 C 46 -2, 58 12, 60 35 C 64 25, 58 8, 64 6 C 70 4, 72 20, 68 40 C 72 32, 70 18, 75 18 C 80 18, 76 34, 70 45"
                                    fill="url(#birdBodyGrad)"
                                    stroke="#cbd5e1"
                                    strokeWidth="0.75"
                                    strokeLinecap="round"
                                />
                            </g>
                        </svg>
                    </div>
                </>
            )}
            <div
                id={styles.envelopeContainer}
                className={`${styles.envelopeContainer} ${introActive ? styles.envelopeIntro : ''}`}
                style={{ '--envelope-background-color': envelopeColor } as React.CSSProperties}
                onClick={animateEnvelope}
            >
                <div id={styles['baseEnvelope']}>
                    <div id={styles['envelopeFrontContainer']} style={{ transform: !isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)', transition: 'transform 1s 1s' }} >
                        <div className={`${styles.cardContainer} ${isOpen ? styles.cardContainerOpen : ''}`}>

                            {coverImage ? (
                                <img src={coverImage} alt="Card Cover" className={styles.cardImage} />
                            ) : (
                                <div className={styles.cardImage}>
                                    {children}
                                </div>

                            )}
                        </div>

                        <svg
                            id={styles.sealTop}
                            key={liningImage}
                            viewBox="0 0 708 277"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            className={styles.sealTop}
                            style={{ transform: !isOpen ? 'rotateX(180deg)' : 'rotateX(0deg)', transition: 'transform  1s' }}
                        >
                            <path
                                d="M1 276.735C1 268.511 1.49586 263.665 3.4192 260.066C6.02638 255.193 9.9498 252.868 15.4121 249.081C53.8531 222.386 223.748 102.865 312.204 41.4467C330.147 28.992 339.233 25 354.381 25C369.528 25 378.615 28.992 396.557 41.4467C485.014 102.857 654.908 222.386 693.349 249.081C698.812 252.868 702.735 255.193 705.343 260.066C707.266 263.656 707.762 268.511 707.762 276.735H1Z"
                                fill={fillStyle}
                            />
                            <mask id="mask0_35_218" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="24" width="707" height="253">
                                <path
                                    d="M0 277C0 268.768 0.496027 263.916 2.42001 260.313C5.02808 255.436 8.95282 253.108 14.4169 249.317C52.871 222.593 222.823 102.947 311.309 41.4636C329.258 28.9958 338.347 24.9996 353.5 24.9996C368.652 24.9996 377.742 28.9958 395.691 41.4636C484.177 102.938 654.129 222.593 692.583 249.317C698.047 253.108 701.972 255.436 704.58 260.313C706.504 263.907 707 268.768 707 277H0Z"
                                    fill={fillStyle}
                                />
                            </mask>
                            <g mask="url(#mask0_35_218)">
                                <rect x="-165" y="-154" width="1190.16" height="793.54" fill="url(#pattern0_35_218)" />
                            </g>
                            <path
                                d="M311.301 63.5044C329.249 51.0664 341.708 40.7541 353.491 40.7541C365.275 40.7541 377.734 51.0579 395.682 63.5044C469.931 114.964 601.497 232.873 664.655 277.008L707 277.008C707 268.796 706.504 263.956 704.58 260.362C701.972 255.496 698.047 253.182 692.583 249.392C654.129 222.732 484.177 77.7608 395.691 16.4244C377.742 3.98634 368.652 -0.000304268 353.5 -0.000305593C338.348 -0.000306917 329.258 3.98634 311.309 16.4244C222.823 77.7522 52.8711 222.732 14.417 249.392C8.95293 253.174 5.02803 255.496 2.41996 260.361C0.495975 263.947 -1.68656e-07 268.796 -8.86601e-07 277.008L42.3447 277.008C105.503 232.873 237.078 114.964 311.309 63.5044L311.301 63.5044Z"
                                fill={fillStyle}
                            />
                            <defs>
                                <linearGradient id="customEnvelopeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={gradStart} />
                                    <stop offset="100%" stopColor={gradEnd} />
                                </linearGradient>
                                <pattern id="pattern0_35_218" patternContentUnits="objectBoundingBox" width="1" height="1">
                                    <use xlinkHref="#image0_35_218" transform="scale(0.000244141 0.000366166)" />
                                </pattern>
                                {/* DYNAMIC LINING IMAGE */}
                                <image
                                    id="image0_35_218"
                                    width="4096"
                                    height="2731"
                                    preserveAspectRatio="none"
                                    href={liningImage}
                                />
                            </defs>
                        </svg>

                        {/* Front face of the top flap */}
                        <svg
                            id={styles.sealFront}
                            className={styles.sealFront}

                            viewBox="0 0 707 252"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ transform: !isOpen ? 'rotateX(0deg)' : 'rotateX(-180deg)', transition: 'transform  1s' }}
                        >
                            <g filter="url(#filter0_i_35_215)">
                                <path
                                    d="M0 0C0 8.22332 0.495755 13.0701 2.41909 16.6689C5.02628 21.5413 8.9499 23.8664 14.4122 27.6533C52.8532 54.3491 222.748 173.869 311.204 235.288C329.147 247.743 338.233 251.735 353.381 251.735C368.528 251.735 377.615 247.743 395.557 235.288C484.014 173.878 653.908 54.3491 692.349 27.6533C697.811 23.8664 701.735 21.5413 704.342 16.6689C706.266 13.0787 706.761 8.22332 706.761 0H0Z"
                                    fill={fillStyle}
                                />
                                <path
                                    d="M0 0C0 8.22332 0.495755 13.0701 2.41909 16.6689C5.02628 21.5413 8.9499 23.8664 14.4122 27.6533C52.8532 54.3491 222.748 173.869 311.204 235.288C329.147 247.743 338.233 251.735 353.381 251.735C368.528 251.735 377.615 247.743 395.557 235.288C484.014 173.878 653.908 54.3491 692.349 27.6533C697.811 23.8664 701.735 21.5413 704.342 16.6689C706.266 13.0787 706.761 8.22332 706.761 0H0Z"
                                    fill="url(#glassSheenFront)"
                                />
                            </g>
                            <defs>
                                <linearGradient id="customEnvelopeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor={gradStart} />
                                    <stop offset="100%" stopColor={gradEnd} />
                                </linearGradient>
                                <linearGradient id="glassSheenFront" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
                                    <stop offset="30%" stopColor="#ffffff" stopOpacity="0.1" />
                                    <stop offset="60%" stopColor="#ffffff" stopOpacity="0" />
                                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.12" />
                                </linearGradient>
                                <filter id="filter0_i_35_215" x="0" y="-12.8222" width="706.762" height="264.557" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feOffset dy="-14" />
                                    <feGaussianBlur stdDeviation="6.41112" />
                                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
                                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_35_215" />
                                </filter>
                            </defs>
                        </svg>

                        <div className={styles.envelopeFrontInner}>
                            {/* Lining visible inside the envelope body */}
                            <svg
                                id={styles.envelopeLiningBottom}
                                className={styles.envelopeLiningBottom}
                                key={liningImage}
                                viewBox="0 0 708 252"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                            >
                                <path
                                    d="M706.762 0.273248C706.762 8.49658 706.266 13.3434 704.343 16.9422C701.735 21.8146 697.812 24.1397 692.35 27.9265C653.909 54.6224 484.014 174.143 395.557 235.561C377.615 248.016 368.528 252.008 353.381 252.008C338.234 252.008 329.147 248.016 311.204 235.561C222.748 174.151 52.8534 54.6224 14.4123 27.9265C8.95002 24.1396 5.02639 21.8145 2.41921 16.9421C0.49587 13.3519 1.04723e-05 8.49651 1.11912e-05 0.273186L706.762 0.273248Z"
                                    fill={fillStyle}
                                />
                                <mask id="mask0_42_262" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="708" height="253">
                                    <path
                                        d="M707.762 0.0083095C707.762 8.2403 707.266 13.0922 705.342 16.6948C702.734 21.5724 698.809 23.8999 693.345 27.6907C654.891 54.4148 484.939 174.061 396.453 235.544C378.504 248.012 369.414 252.008 354.262 252.008C339.109 252.008 330.02 248.012 312.071 235.544C223.585 174.07 53.6329 54.4147 15.1789 27.6907C9.71479 23.8999 5.78983 21.5723 3.18176 16.6947C1.25778 13.1007 0.761755 8.24024 0.761756 0.00824769L707.762 0.0083095Z"
                                        fill={fillStyle}
                                    />
                                </mask>
                                <g mask="url(#mask0_42_262)">
                                    <rect x="872.762" y="431.008" width="1190.16" height="793.54" transform="rotate(-180 872.762 431.008)" fill="url(#pattern0_42_262)" />
                                </g>
                                <defs>
                                    <linearGradient id="customEnvelopeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor={gradStart} />
                                        <stop offset="100%" stopColor={gradEnd} />
                                    </linearGradient>
                                    <pattern id="pattern0_42_262" patternContentUnits="objectBoundingBox" width="1" height="1">
                                        <use xlinkHref="#image0_42_262" transform="scale(0.000244141 0.000366166)" />
                                    </pattern>
                                    {/* DYNAMIC LINING IMAGE */}
                                    <image
                                        id="image0_42_262"
                                        width="4096"
                                        height="2731"
                                        preserveAspectRatio="none"
                                        href={liningImage}
                                    />
                                </defs>
                            </svg>

                            {/* Main body of the envelope */}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"

                                fill="none"
                                id={styles.envelopeBottom}
                                className={styles.envelopeBottom}
                                viewBox="0 0 708 488"
                            >
                                <path
                                    fill={fillStyle}
                                    d="M0 0c0 8.218.496 13.062 2.423 16.658 2.612 4.87 6.543 7.193 12.014 10.977 38.509 26.68 208.701 146.123 297.312 207.502 17.974 12.446 27.077 16.435 42.251 16.435 15.174 0 24.277-3.989 42.251-16.435 88.611-61.371 258.802-180.822 297.311-207.501 5.471-3.785 9.402-6.109 12.014-10.978C707.503 13.07 708 8.218 708 0v487H0V0Z"
                                />
                                <path
                                    fill="url(#glassSheenBottom)"
                                    d="M0 0c0 8.218.496 13.062 2.423 16.658 2.612 4.87 6.543 7.193 12.014 10.977 38.509 26.68 208.701 146.123 297.312 207.502 17.974 12.446 27.077 16.435 42.251 16.435 15.174 0 24.277-3.989 42.251-16.435 88.611-61.371 258.802-180.822 297.311-207.501 5.471-3.785 9.402-6.109 12.014-10.978C707.503 13.07 708 8.218 708 0v487H0V0Z"
                                />
                                <path
                                    fill="url(#b)"
                                    style={{ fillOpacity: '.2' }}
                                    d="M0 0c0 8.218.496 13.062 2.423 16.658 2.612 4.87 6.543 7.193 12.014 10.977 38.509 26.68 208.701 146.123 297.312 207.502 17.974 12.446 27.077 16.435 42.251 16.435 15.174 0 24.277-3.989 42.251-16.435 88.611-61.371 258.802-180.822 297.311-207.501 5.471-3.785 9.402-6.109 12.014-10.978C707.503 13.07 708 8.218 708 0v487H0V0Z"
                                />
                                <g filter="url(#c)">
                                    <path
                                        fill={fillStyle}
                                        d="M682.16 452.435c-38.202-27.037-198.283-138.223-286.098-198.949-10.215-7.061-22.012-13.84-42.681-13.84-20.67 0-32.466 6.77-42.681 13.84-87.815 60.726-247.897 171.912-286.098 198.949C21.772 454.436 0 468.899 0 487.312h706.761c0-18.413-21.772-32.876-24.601-34.877Z"
                                    />
                                    <path
                                        fill="url(#glassSheenBottom)"
                                        d="M682.16 452.435c-38.202-27.037-198.283-138.223-286.098-198.949-10.215-7.061-22.012-13.84-42.681-13.84-20.67 0-32.466 6.77-42.681 13.84-87.815 60.726-247.897 171.912-286.098 198.949C21.772 454.436 0 468.899 0 487.312h706.761c0-18.413-21.772-32.876-24.601-34.877Z"
                                    />
                                </g>
                                <defs>
                                    <linearGradient id="customEnvelopeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor={gradStart} />
                                        <stop offset="100%" stopColor={gradEnd} />
                                    </linearGradient>
                                    <linearGradient id="glassSheenBottom" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.3" />
                                        <stop offset="35%" stopColor="#ffffff" stopOpacity="0.08" />
                                        <stop offset="65%" stopColor="#ffffff" stopOpacity="0" />
                                        <stop offset="100%" stopColor="#ffffff" stopOpacity="0.12" />
                                    </linearGradient>
                                    <linearGradient id="b" x1="354" x2="354" y1="0" y2="487" gradientUnits="userSpaceOnUse">
                                        <stop offset=".596" style={{ stopOpacity: '0' }} />
                                        <stop offset="1" style={{ stopOpacity: '.3' }} />
                                    </linearGradient>
                                    <filter id="c" width="716.961" height="257.866" x="-5.1" y="233.546" colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
                                        <feOffset dy="-1" />
                                        <feGaussianBlur stdDeviation="2.55" />
                                        <feComposite in2="hardAlpha" operator="out" />
                                        <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
                                        <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_97_139" />
                                        <feBlend in="SourceGraphic" in2="effect1_dropShadow_97_139" result="shape" />
                                        <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
                                        <feOffset dy="1" />
                                        <feGaussianBlur stdDeviation="2" />
                                        <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
                                        <feColorMatrix values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.15 0" />
                                        <feBlend in2="shape" result="effect2_innerShadow_97_139" />
                                    </filter>
                                </defs>
                            </svg>
                        </div>
                    </div>

                    {/* Back of the envelope with text */}
                    <div
                        id={styles.envelopeBack}
                        style={{
                            fontFamily: fontFamily,
                            transform: !isFlipped ? 'rotateY(0deg)' : 'rotateY(-180deg)',
                            transition: 'transform 1s 1s, opacity 0.5s 1.5s',
                            pointerEvents: !isFlipped ? 'auto' : 'none',
                            opacity: !isFlipped ? 1 : 0,
                        }}
                    >
                        <div className={styles.backRelative}>
                            <div className={styles.envelopeLogoWrapper}>
                                <img
                                    className={styles.envelopeLogoImg}
                                    src={envelopeLogo}
                                    alt="Envelope Logo"
                                />
                            </div>
                            <div className={styles.logoCenter}>
                                <img className={styles.logoImg} src={stampUrl} alt="Stamp" />
                            </div>
                        </div>
                        <div className={styles.titleContainer}>
                            <div
                                style={{ whiteSpace: 'pre-wrap', color: textColor }}
                                className={`${fontFamily} text-2xl`}
                            >
                                {title || 'Your Envelope Title Here'}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Control Button */}
            {isRotator && !hideControl && (
                <button className={styles.controlButton} onClick={() => {
                    animateEnvelope()
                }} aria-label="Toggle Envelope">
                    {isOpen ? <FaEnvelope size={20} color="#333" /> :
                        <FaEnvelopeOpen size={20} color="#333" />}
                </button>
            )}
        </div>
    );
};

export default Envelope;
